/**
 * תכנון ופיתוח מוצרים — the vocabulary the module shares.
 *
 * Codes here, DTOs in `productPlanning.projects.ts` / `.tasks.ts` / `.chat.ts`. The `chips` that
 * colour these codes live on the frontend (`pages/dashboard/Planning/planningTones.ts`), because a
 * colour is a UI decision and this file is imported by the backend.
 *
 * No React, no MUI, no browser globals: the backend imports this file directly through the
 * `@malash-office/types/product-planning` alias.
 *
 * **Codes are English, labels are Hebrew** (ADR-017). This module ships after Product Hub and
 * follows it: `stage`, `status` and `kind` recur across five tables here, and `הצעות מחיר` would
 * name both a stage and a status if the labels were the stored values.
 *
 * ## Why `stage` and `status` are two axes and not one
 *
 * The approved mockup carries two lists that overlap: a six-value "שלב" chip in the table, and a
 * seven-value dropdown in the project header that mixes stage-like values (`הצעות מחיר`, `גרפיקה`)
 * with lifecycle ones (`מושהה`, `מבוטל`). Stored as one column they would collide — a project put
 * `מושהה` would lose the stage it was paused at. So the seven are split into their two real axes:
 * **`stage`** is where in the workflow the project is (seven values, `גרפיקה` recovered from the
 * dropdown), and **`status`** is whether it is running at all (five values). Both mockup columns
 * keep rendering; nothing in it is lost. `in_production` (בייצור) sits immediately after `ready`
 * and is the last status in that order — nothing in the workflow follows it. `on_hold` and
 * `cancelled` stay exits beside it, not later stages.
 */

import { PhOption, phOptions } from './productHub.core';

/* ── stage — where in the workflow ────────────────────────────────────────────────────────── */

export const PLANNING_STAGES = [
  'request',
  'spec',
  'sourcing',
  'quotes',
  'graphics',
  'sample',
  'costing',
  'approval'
] as const;
export type PlanningStage = (typeof PLANNING_STAGES)[number];

export const PLANNING_STAGE_LABELS: Record<PlanningStage, string> = {
  request: 'בקשה מלקוח',
  spec: 'אפיון ראשוני',
  sourcing: 'איתור ספקים',
  quotes: 'הצעות מחיר',
  graphics: 'גרפיקה',
  sample: 'בדיקת דוגמה',
  costing: 'תמחור',
  // "ממתין לאישור סופי" and not "אישור סופי" (by request, 2026-10-16): the project is waiting on
  // the decision, it has not had it — read as "approved" it sounded like the ready gate had passed.
  approval: 'ממתין לאישור סופי'
};
export const PLANNING_STAGE_OPTIONS = phOptions(PLANNING_STAGES, PLANNING_STAGE_LABELS);

/* ── status — whether it is running at all ────────────────────────────────────────────────── */

export const PLANNING_PROJECT_STATUSES = ['active', 'ready', 'in_production', 'on_hold', 'cancelled'] as const;
export type PlanningProjectStatus = (typeof PLANNING_PROJECT_STATUSES)[number];

export const PLANNING_PROJECT_STATUS_LABELS: Record<PlanningProjectStatus, string> = {
  active: 'פעיל',
  ready: 'מוכן לייצור',
  // Last status in the workflow. Placed immediately after ready; hold and cancel are exits, not
  // successors. The column is STRING(16) and this code is 14 characters, so no migration is required.
  in_production: 'בייצור',
  on_hold: 'מושהה',
  cancelled: 'מבוטל'
};
export const PLANNING_PROJECT_STATUS_OPTIONS = phOptions(PLANNING_PROJECT_STATUSES, PLANNING_PROJECT_STATUS_LABELS);

/** A project nobody is working on any more. Excluded from the KPI tiles and from "פעילים". */
export const PLANNING_CLOSED_STATUSES: readonly PlanningProjectStatus[] = ['cancelled'];

/**
 * Not open planning work. The late pill and the attention panel exclude these: `ready` has left
 * the workflow, `in_production` is the last status (nothing follows it), and `cancelled` is closed.
 * Team load and the budget still include `in_production` — that work is real, it is just no longer
 * "late planning".
 */
export const PLANNING_NOT_OPEN_STATUSES: readonly PlanningProjectStatus[] = ['ready', 'in_production', 'cancelled'];

/* ── quote status — has this offer been decided on ────────────────────────────────────────── */

/**
 * Where one supplier quote stands: nobody has ruled on it, it is **the** offer, or it is out.
 *
 * A third axis over the quote and not a second meaning of the project's `stage`: a project sits in
 * `quotes` while four offers are on the table, and each of those four has its own answer.
 *
 * **`approved` is the module's single decision point.** It replaced the earlier "בחר ספק", which
 * wrote `PlanningProjectItems.selectedQuoteId` and nothing else — two mechanisms that could
 * disagree about which price a factory order was placed at. Approving a quote now writes both, and
 * only an approved quote is carried into a production order (`isForOrder`).
 *
 * `rejected` earns its place over a plain two-state flag: an offer that was read and turned down
 * looks exactly like one nobody has opened yet, and next round somebody asks that supplier again.
 * `cancelled` is the offer that was withdrawn rather than turned down — the supplier pulled it, or
 * the request behind it was dropped. `pending` is labelled "חדש": the offer arrived and nobody has
 * ruled on it yet.
 *
 * `from_supplier` is the offer a supplier typed on the public link (`quoteRequest.types.ts`). It
 * is still an open offer — not closed, not approved — and the label is the one the card shows so
 * a price that arrived by itself is not mistaken for one somebody on the team typed. It does not
 * become an order price, a budget estimate, or a kit total until somebody approves it. A quote
 * typed on the card (`pending`) is unchanged: a lone one still carries without that click.
 */
export const PLANNING_QUOTE_STATUSES = ['pending', 'from_supplier', 'approved', 'rejected', 'cancelled'] as const;
export type PlanningQuoteStatus = (typeof PLANNING_QUOTE_STATUSES)[number];

export const PLANNING_QUOTE_STATUS_LABELS: Record<PlanningQuoteStatus, string> = {
  pending: 'חדש',
  from_supplier: 'הצעה חדשה מהספק',
  approved: 'מאושר',
  rejected: 'נדחה',
  cancelled: 'בוטל'
};

/**
 * The statuses that take an offer out of the race. A rejected offer is one we turned down; a
 * cancelled one is one the supplier (or we) withdrew. Neither is ever ordered from — not even as
 * the only offer in its scope — and neither counts as a competitor that still needs deciding.
 */
export const PLANNING_QUOTE_CLOSED_STATUSES: readonly PlanningQuoteStatus[] = ['rejected', 'cancelled'];
export const PLANNING_QUOTE_STATUS_OPTIONS = phOptions(PLANNING_QUOTE_STATUSES, PLANNING_QUOTE_STATUS_LABELS);

/** A price typed on the public link that nobody on the team has approved yet. */
export const isUnreviewedLinkQuote = (status: string): boolean => status === 'from_supplier';

/**
 * The main product — the project itself (its name, SKU and planned quantity) read as the kit's first
 * item. It has no `PlanningProjectItems` row: whatever is filed against no component (`itemId: null`
 * on a quote, a file or a customer-quote line) is filed against it, and every screen that lists the
 * items lists it first. Read rather than stored, so it holds for every project ever opened without a
 * migration. It replaces the earlier "המוצר כולו" / "המארז כולו", by the product manager's request.
 */
export const PLANNING_MAIN_PRODUCT_LABEL = 'מוצר ראשי';

/* ── origin — who opened the card ─────────────────────────────────────────────────────────── */

/**
 * Who opened the project: somebody on the team, or the customer through the portal.
 *
 * A third axis and not a stage, because it never changes. `stage` moves, `status` moves; where a
 * card came from is a fact about its birth, and two rules read it forever after: the customer quote
 * section only opens on a `customer` card, and `request` is a stage only a `customer` card starts in.
 */
export const PLANNING_PROJECT_ORIGINS = ['internal', 'customer'] as const;
export type PlanningProjectOrigin = (typeof PLANNING_PROJECT_ORIGINS)[number];

export const PLANNING_PROJECT_ORIGIN_LABELS: Record<PlanningProjectOrigin, string> = {
  internal: 'נפתח אצלנו',
  customer: 'בקשה מלקוח'
};
export const PLANNING_PROJECT_ORIGIN_OPTIONS = phOptions(PLANNING_PROJECT_ORIGINS, PLANNING_PROJECT_ORIGIN_LABELS);

/**
 * How a customer request reached us — the portal's form, or the customer's own system through
 * `/api/customer-api/v1` (docs/product-planning/CUSTOMER-API.md). Meaningful on `origin: 'customer'`
 * only; an internal card keeps the default and nobody reads it.
 */
export const PLANNING_SUBMISSION_CHANNELS = ['portal', 'api'] as const;
export type PlanningSubmissionChannel = (typeof PLANNING_SUBMISSION_CHANNELS)[number];

export const PLANNING_SUBMISSION_CHANNEL_LABELS: Record<PlanningSubmissionChannel, string> = {
  portal: 'פורטל',
  api: 'API'
};

/* ── project type — a table, but its four rows carry a code the rules branch on ──────────── */

/**
 * The four project types, seeded rows in `PlanningProjectTypes`.
 *
 * **The list is closed.** It was runtime-extensible once ("+ הוספת סוג פרויקט חדש"); the requester
 * removed that affordance, because one rule branches on *which* type it is — an `upgrade` or a
 * `production_round` **must** name the development project it continues — and a type somebody typed
 * a Hebrew name for could not carry that rule. It stays a table rather than becoming an enum only
 * because every project row points at it by id.
 *
 * A row with `code: null` can still exist in an environment where one was added before the button
 * went away. It behaves like `new`, the permissive case.
 */
export const PLANNING_PROJECT_TYPE_CODES = ['new', 'upgrade', 'production_round', 'other'] as const;
export type PlanningProjectTypeCode = (typeof PLANNING_PROJECT_TYPE_CODES)[number];

export const PLANNING_PROJECT_TYPE_LABELS: Record<PlanningProjectTypeCode, string> = {
  new: 'מוצר חדש',
  upgrade: 'שינוי / שדרוג מוצר קיים',
  production_round: 'סבב ייצור חדש',
  other: 'אחר'
};

/**
 * Types whose project **must** name an earlier development project to continue.
 *
 * A change to an existing product and a new production round of one are both *the same product
 * again* — so the new card is opened as a copy of that project's history: its name, its components,
 * its suppliers and their prices, and its files. The source is a **`PlanningProjects` row and never
 * a catalogue product** (the requester was explicit): what is being continued is the development
 * work, and the catalogue holds none of it — no quotes, no supplier prices, no artwork revisions.
 *
 * The copy is taken once, at creation. It is not a live link: the two projects are separate rounds
 * and each is edited on its own from that moment, which is the whole reason a round is a new card.
 *
 * `new` and `other` need nothing — a product in development usually does not exist anywhere yet.
 */
export const requiresSourceProject = (code: PlanningProjectTypeCode | null): boolean =>
  code === 'upgrade' || code === 'production_round';

/**
 * What is copied from the source project into the new card, and what deliberately is not.
 *
 * Tasks and chat are left behind on purpose (the requester named both): a task board is who is
 * doing what *now* and a conversation is a record of one round's decisions — copied forward they
 * would arrive as work already assigned and as agreements nobody in the new round made. Sample
 * reviews go the same way for the same reason: a verdict belongs to the sample it was given on.
 */
export const PLANNING_CLONED_SECTIONS = ['name', 'items', 'quotes', 'files'] as const;

/* ── tasks ────────────────────────────────────────────────────────────────────────────────── */

export const PLANNING_TASK_STATUSES = ['todo', 'doing', 'done'] as const;
export type PlanningTaskStatus = (typeof PLANNING_TASK_STATUSES)[number];

/** The three board columns, in the order the mockup shows them. */
export const PLANNING_TASK_STATUS_LABELS: Record<PlanningTaskStatus, string> = {
  todo: 'לעשות',
  doing: 'בתהליך / ממתין',
  done: 'הושלם'
};
export const PLANNING_TASK_STATUS_OPTIONS = phOptions(PLANNING_TASK_STATUSES, PLANNING_TASK_STATUS_LABELS);

export const PLANNING_TASK_KINDS = ['work', 'approval', 'decision', 'check', 'followup', 'waiting'] as const;
export type PlanningTaskKind = (typeof PLANNING_TASK_KINDS)[number];

export const PLANNING_TASK_KIND_LABELS: Record<PlanningTaskKind, string> = {
  work: 'עבודה',
  approval: 'אישור',
  decision: 'החלטה',
  check: 'בדיקה',
  followup: 'מעקב',
  waiting: 'ממתין'
};
export const PLANNING_TASK_KIND_OPTIONS = phOptions(PLANNING_TASK_KINDS, PLANNING_TASK_KIND_LABELS);

/**
 * The buckets the "המשימות שלי" screen groups into, in the order it shows them.
 *
 * Derived, never stored: the same task moves between them as the date passes. `waiting_others` is
 * last on purpose — it is the only bucket the reader cannot act on.
 */
export const PLANNING_TASK_BUCKETS = ['waiting_me', 'late', 'this_week', 'waiting_others'] as const;
export type PlanningTaskBucket = (typeof PLANNING_TASK_BUCKETS)[number];

export const PLANNING_TASK_BUCKET_LABELS: Record<PlanningTaskBucket, string> = {
  waiting_me: 'ממתין לי',
  late: 'באיחור',
  this_week: 'השבוע',
  waiting_others: 'ממתין לאחרים'
};

/* ── files ────────────────────────────────────────────────────────────────────────────────── */

/**
 * What a file on a development project *is*, in the words the team uses for it.
 *
 * The order is the order of the drawer's dropdown. `quote` is deliberately absent: an offer from a
 * supplier is a `PlanningQuote` row with a price and a supplier behind it, and a file category by
 * the same name invited the two to disagree about what the project costs.
 */
export const PLANNING_FILE_CATEGORIES = [
  'sample',
  'spec',
  'definition',
  'standard',
  'print',
  'production',
  'customer'
] as const;
export type PlanningFileCategory = (typeof PLANNING_FILE_CATEGORIES)[number];

export const PLANNING_FILE_CATEGORY_LABELS: Record<PlanningFileCategory, string> = {
  sample: 'דוגמאות',
  spec: 'מפרט',
  definition: 'אפיון',
  standard: 'תקן',
  print: 'קובץ לדפוס',
  production: 'תוכנית יצור',
  customer: 'קבצים מהלקוח'
};
export const PLANNING_FILE_CATEGORY_OPTIONS = phOptions(PLANNING_FILE_CATEGORIES, PLANNING_FILE_CATEGORY_LABELS);

/* ── sample review ────────────────────────────────────────────────────────────────────────── */

export const PLANNING_SAMPLE_DECISIONS = ['approve', 'changes', 'reject'] as const;
export type PlanningSampleDecision = (typeof PLANNING_SAMPLE_DECISIONS)[number];

/** Verbs, because the only place these appear is the three buttons in the review drawer. */
export const PLANNING_SAMPLE_DECISION_LABELS: Record<PlanningSampleDecision, string> = {
  approve: 'אשר',
  changes: 'בקש תיקונים',
  reject: 'דחה'
};
export const PLANNING_SAMPLE_DECISION_OPTIONS = phOptions(PLANNING_SAMPLE_DECISIONS, PLANNING_SAMPLE_DECISION_LABELS);

/** Notes are optional only when approving; the other two decisions owe the supplier a reason. */
export const sampleNotesRequired = (decision: PlanningSampleDecision): boolean => decision !== 'approve';

/* ── chat ─────────────────────────────────────────────────────────────────────────────────── */

export const PLANNING_MESSAGE_KINDS = ['message', 'system'] as const;
export type PlanningMessageKind = (typeof PLANNING_MESSAGE_KINDS)[number];

export const PLANNING_MESSAGE_KIND_LABELS: Record<PlanningMessageKind, string> = {
  message: 'הודעה',
  system: 'עדכון מערכת'
};

/**
 * Who a message is written to — the team, or the customer reading the same thread from the portal.
 *
 * One thread and not two, because the requester asked for exactly the chat that exists today. The
 * column is what keeps it one thread safely: the portal reads `customer` rows only, so an internal
 * aside never leaves the building, and the team keeps reading everything in one scroller.
 *
 * A human message a team member writes on a shared card defaults to `customer` — that was the
 * requester's call, and it is the right one for a thread whose whole purpose is talking to them.
 * A **system** line defaults the other way, through `PLANNING_SYSTEM_EVENT_AUDIENCE`.
 */
export const PLANNING_MESSAGE_AUDIENCES = ['internal', 'customer'] as const;
export type PlanningMessageAudience = (typeof PLANNING_MESSAGE_AUDIENCES)[number];

export const PLANNING_MESSAGE_AUDIENCE_LABELS: Record<PlanningMessageAudience, string> = {
  internal: 'פנימי',
  customer: 'גלוי ללקוח'
};
export const PLANNING_MESSAGE_AUDIENCE_OPTIONS = phOptions(
  PLANNING_MESSAGE_AUDIENCES,
  PLANNING_MESSAGE_AUDIENCE_LABELS
);

/* ── shared shapes ────────────────────────────────────────────────────────────────────────── */

/** A person as every planning payload names one: enough to render an avatar without a second call. */
export interface PlanningUserRef {
  id: number;
  fullName: string;
}

/** A supplier as the quote cards name one. Mirrors the columns `Suppliers` already has. */
export interface PlanningSupplierRef {
  id: number;
  name: string;
  contactName: string | null;
  country: string | null;
}

/** A runtime-extensible lookup row — project types and project categories are both this shape. */
export interface PlanningLookupDto {
  id: number;
  name: string;
  /** Present only on the seeded rows; the rules in this file branch on it. */
  code: PlanningProjectTypeCode | null;
  isSystem: boolean;
  /**
   * The production customer a project *category* belongs to. Null when the category is internal.
   * Absent on project types — those are not scoped to a customer.
   */
  productionCustomerId?: number | null;
}

export const planningLookupOptions = (rows: readonly PlanningLookupDto[]): PhOption<string>[] =>
  rows.map(row => ({ value: String(row.id), label: row.name }));

/* ── error codes ──────────────────────────────────────────────────────────────────────────── */

/**
 * Stable codes for the failures the UI reacts to. Hebrew messages may be reworded; these may not.
 * `PLN_` prefix so a code is recognisable in a log line without its context.
 */
export const PLANNING_ERROR_CODES = {
  PROJECT_NOT_FOUND: 'PLN_PROJECT_NOT_FOUND',
  SOURCE_REQUIRED_FOR_TYPE: 'PLN_SOURCE_REQUIRED_FOR_TYPE',
  SOURCE_PROJECT_NOT_FOUND: 'PLN_SOURCE_PROJECT_NOT_FOUND',
  CUSTOMER_REQUIRED_FOR_TYPE: 'PLN_CUSTOMER_REQUIRED_FOR_TYPE',
  PRODUCT_LINK_NOT_ALLOWED: 'PLN_PRODUCT_LINK_NOT_ALLOWED',
  QUOTE_ITEM_MISMATCH: 'PLN_QUOTE_ITEM_MISMATCH',
  QUOTE_NOT_FOR_ITEM: 'PLN_QUOTE_NOT_FOR_ITEM',
  SAMPLE_NOTES_REQUIRED: 'PLN_SAMPLE_NOTES_REQUIRED',
  LOOKUP_IN_USE: 'PLN_LOOKUP_IN_USE',
  TYPE_NOT_FOUND: 'PLN_TYPE_NOT_FOUND',
  FILE_GROUP_MISMATCH: 'PLN_FILE_GROUP_MISMATCH',
  FILE_ITEM_MISMATCH: 'PLN_FILE_ITEM_MISMATCH',
  LOOKUP_NAME_TAKEN: 'PLN_LOOKUP_NAME_TAKEN',
  SUPPLIER_TYPE_NOT_FOUND: 'PLN_SUPPLIER_TYPE_NOT_FOUND',
  LOOKUP_CUSTOMER_MISMATCH: 'PLN_LOOKUP_CUSTOMER_MISMATCH',
  LOOKUP_CUSTOMER_NOT_FOUND: 'PLN_LOOKUP_CUSTOMER_NOT_FOUND',
  SUPPLIER_HAS_ORDERS: 'PLN_SUPPLIER_HAS_ORDERS',
  SUPPLIER_NUMBER_TAKEN: 'PLN_SUPPLIER_NUMBER_TAKEN',
  TAG_NOT_FOUND: 'PLN_TAG_NOT_FOUND',
  CATEGORY_NOT_FOUND: 'PLN_CATEGORY_NOT_FOUND',
  STORAGE_UNAVAILABLE: 'PLN_STORAGE_UNAVAILABLE',
  CUSTOMER_QUOTE_NOT_FOUND: 'PLN_CUSTOMER_QUOTE_NOT_FOUND',
  CUSTOMER_QUOTE_LOCKED: 'PLN_CUSTOMER_QUOTE_LOCKED',
  CUSTOMER_QUOTE_ITEM_MISMATCH: 'PLN_CUSTOMER_QUOTE_ITEM_MISMATCH',
  PROJECT_NOT_SHARED: 'PLN_PROJECT_NOT_SHARED',
  PORTAL_NO_ACCESS: 'PLN_PORTAL_NO_ACCESS',
  PORTAL_USER_PHONE_REQUIRED: 'PLN_PORTAL_USER_PHONE_REQUIRED',
  PORTAL_USER_PHONE_INVALID: 'PLN_PORTAL_USER_PHONE_INVALID',
  PORTAL_USER_PHONE_TAKEN: 'PLN_PORTAL_USER_PHONE_TAKEN',
  PORTAL_USER_NOT_LINKED: 'PLN_PORTAL_USER_NOT_LINKED',
  QUOTE_APPROVAL_REQUIRED: 'PLN_QUOTE_APPROVAL_REQUIRED',
  /** The order is past any customer edit (מוכן לייצור onward), or the action needs the early window. */
  PORTAL_ORDER_LOCKED: 'PLN_PORTAL_ORDER_LOCKED',
  /** A partial edit (past אפיון ראשוני) tried to change the product, a quantity or a locked spec. */
  PORTAL_ORDER_FIELD_LOCKED: 'PLN_PORTAL_ORDER_FIELD_LOCKED',
  /** A component id in a portal edit does not belong to that order. */
  PORTAL_ITEM_NOT_ON_ORDER: 'PLN_PORTAL_ITEM_NOT_ON_ORDER',
  /** Removing the component would take a quote, a file, a supplier quote request or a production order with it. */
  PORTAL_ITEM_ATTACHED: 'PLN_PORTAL_ITEM_ATTACHED',
  QUOTE_REQUEST_SUPPLIER: 'PLN_QUOTE_REQUEST_SUPPLIER',
  QUOTE_REQUEST_NO_EMAIL: 'PLN_QUOTE_REQUEST_NO_EMAIL',
  QUOTE_REQUEST_MAIL_NOT_CONFIGURED: 'PLN_QUOTE_REQUEST_MAIL_NOT_CONFIGURED',
  QUOTE_REQUEST_SEND_FAILED: 'PLN_QUOTE_REQUEST_SEND_FAILED',
  QUOTE_REQUEST_ITEMS: 'PLN_QUOTE_REQUEST_ITEMS',
  QUOTE_REQUEST_FILES: 'PLN_QUOTE_REQUEST_FILES',
  QUOTE_REQUEST_CLOSED: 'PLN_QUOTE_REQUEST_CLOSED',
  QUOTE_REQUEST_NOT_OPEN: 'PLN_QUOTE_REQUEST_NOT_OPEN'
} as const;
export type PlanningErrorCode = (typeof PLANNING_ERROR_CODES)[keyof typeof PLANNING_ERROR_CODES];

export * from './productPlanning.itemSpec';

/**
 * Which files go to a supplier by default — in a quote request and on a production order
 * (2026-10-21, "לשים לב שנשלח קובץ הפריט"): every file filed under a component, and the project's
 * own files in the categories a factory works from. Anything else stays one tick away.
 */
export const PLANNING_SUPPLIER_FILE_CATEGORIES: readonly PlanningFileCategory[] = [
  'spec',
  'definition',
  'print',
  'production'
];

export const isSupplierFileByDefault = (group: { itemId: number | null; category: string }): boolean =>
  group.itemId !== null || (PLANNING_SUPPLIER_FILE_CATEGORIES as readonly string[]).includes(group.category);
